import "@/publicPath";
import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";
import AppErrorBoundary from "@/components/AppErrorBoundary";
import { shouldRegisterServiceWorker } from "@/lib/environment";

// --- Offline-friendly error handling -----------------------------------
// Imkers gebruiken de app vaak in het veld zonder netwerk. Een onafgevangen
// "Network Error" zou anders het CRA-dev-overlay activeren of een lege React
// boundary triggeren. We onderdrukken specifiek onze gemarkeerde OfflineError
// (zie src/lib/api.js) en losse axios "Network Error" promises terwijl
// navigator.onLine === false. Echte fouten blijven zichtbaar.
function isQuietNetworkError(reasonOrError) {
  if (!reasonOrError) return false;
  if (reasonOrError.isOffline) return true;
  const msg = reasonOrError.message || "";
  const offline = typeof navigator !== "undefined" && navigator.onLine === false;
  if (offline && !reasonOrError.response && (msg === "Network Error" || msg === "Network request failed" || reasonOrError.code === "ERR_NETWORK")) {
    return true;
  }
  return false;
}

window.addEventListener("unhandledrejection", (event) => {
  if (isQuietNetworkError(event.reason)) {
    event.preventDefault();
    event.stopImmediatePropagation?.();
  }
});

window.addEventListener("error", (event) => {
  if (isQuietNetworkError(event.error || event)) {
    event.preventDefault();
    event.stopImmediatePropagation?.();
  }
}, true);

// WKWebView can still emit legacy gesture events even when the viewport is
// non-scalable. Keep all one-finger scrolling intact, but block pinch zoom.
for (const eventName of ["gesturestart", "gesturechange", "gestureend"]) {
  document.addEventListener(eventName, (event) => event.preventDefault(), { passive: false });
}
document.addEventListener("touchmove", (event) => {
  if (event.touches.length > 1) event.preventDefault();
}, { passive: false });

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <AppErrorBoundary>
      <App />
    </AppErrorBoundary>
  </React.StrictMode>,
);

// Service worker stays opt-in until the release flow has explicit update UX.
// This prevents stale dev/beta/mobile WebView bundles from lingering.
if ("serviceWorker" in navigator && shouldRegisterServiceWorker()) {
  let reloadingForUpdate = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (reloadingForUpdate) return;
    reloadingForUpdate = true;
    window.location.reload();
  });
  navigator.serviceWorker
    .register("/sw.js", { updateViaCache: "none" })
    .then((registration) => registration.update())
    .catch(() => {});
} else if ("serviceWorker" in navigator) {
  navigator.serviceWorker.getRegistrations?.().then((regs) => {
    regs.forEach((reg) => reg.unregister());
  }).catch(() => {});
}
